/**
 * OrderDetailModal - Comprehensive order details with tabbed interface
 * Displays order header, positions, delivery tracking, and file management
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  IconButton,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  LocalShipping as DeliveryIcon,
  AttachFile as FileIcon,
  Timeline as StatusIcon
} from '@mui/icons-material';
import { UnifiedOrder, DeliveryLog } from '../../types/unifiedOrderTypes';
import { unifiedOrderService, UnifiedOrderService } from '../../services/unifiedOrderService';
import { useNotification } from '../../contexts/NotificationContext';
import DeliveryManagement from './DeliveryManagement';
import FileManagement from './FileManagement';
import PositionEditor from './PositionEditor';
import StatusProgress from './StatusProgress';
import StatusManagement from './StatusManagement';
import { formatDate } from '../../utils/dateUtils';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`order-tabpanel-${index}`}
      aria-labelledby={`order-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface OrderDetailModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string | null;
  onOrderUpdated?: () => void;
}

export default function OrderDetailModal({ 
  open, 
  onClose, 
  orderId, 
  onOrderUpdated 
}: OrderDetailModalProps) {
  const [tabValue, setTabValue] = useState(0);
  const [order, setOrder] = useState<UnifiedOrder | null>(null);
  const [deliveryHistory, setDeliveryHistory] = useState<DeliveryLog[]>([]);
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  useEffect(() => {
    if (open && orderId) {
      loadOrderDetails();
    }
  }, [open, orderId]);

  const loadOrderDetails = async () => {
    if (!orderId) return;

    try {
      setLoading(true);
      
      // Load order details
      const orderResponse = await unifiedOrderService.getOrder(orderId);
      if (orderResponse.success && orderResponse.data) {
        setOrder(orderResponse.data);
      }

      // Load delivery history
      const deliveryResponse = await unifiedOrderService.getDeliveryHistory(orderId);
      if (deliveryResponse.success && deliveryResponse.data) {
        setDeliveryHistory(deliveryResponse.data);
      }
    } catch (error: any) {
      showNotification('Fehler beim Laden der Bestelldetails: ' + error.message, 'error'); // Error loading order details: ${error.message}
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleOrderUpdate = () => {
    loadOrderDetails();
    onOrderUpdated?.();
  };

  const handleStatusChange = (newStatus: string) => {
    // Update the local order state
    setOrder(prevOrder => prevOrder ? { 
      ...prevOrder, 
      status: newStatus as 'draft' | 'ready' | 'sent' | 'partial' | 'delivered' | 'completed'
    } : null);
    // Trigger parent component update
    onOrderUpdated?.();
  };

  if (!order) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="xl" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh', maxHeight: '90vh' }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6" component="div">
              Bestelldetails: {order.id} {/* Order Details: {order.id} */}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <Chip 
                label={UnifiedOrderService.getOrderTypeDisplayName(order.order_type)}
                color="primary"
                size="small"
              />
              <Chip 
                label={UnifiedOrderService.getStatusDisplayName(order.status)}
                sx={{ 
                  backgroundColor: UnifiedOrderService.getStatusColor(order.status),
                  color: 'white'
                }}
                size="small"
              />
            </Box>
          </Box>
          <IconButton onClick={onClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="order detail tabs">
            <Tab 
              icon={<BusinessIcon />} 
              label="Bestellinfo" /* Order Info */
              id="order-tab-0"
              aria-controls="order-tabpanel-0"
            />
            <Tab 
              icon={<AssignmentIcon />} 
              label="Positionen" /* Positions */
              id="order-tab-1"
              aria-controls="order-tabpanel-1"
            />
            <Tab 
              icon={<DeliveryIcon />} 
              label="Lieferung" /* Delivery */
              id="order-tab-2"
              aria-controls="order-tabpanel-2"
            />
            <Tab 
              icon={<FileIcon />} 
              label="Dateien" /* Files */
              id="order-tab-3"
              aria-controls="order-tabpanel-3"
            />
            <Tab 
              icon={<StatusIcon />} 
              label="Status" /* Status */
              id="order-tab-4"
              aria-controls="order-tabpanel-4"
            />
          </Tabs>
        </Box>

        {/* Order Header Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <StatusProgress status={order.status} />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Bestellinformationen {/* Order Information */}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Bestell-ID {/* Order ID */}
                      </Typography>
                      <Typography variant="body1">
                        {order.id}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Bestellungstyp {/* Order Type */}
                      </Typography>
                      <Typography variant="body1">
                        {UnifiedOrderService.getOrderTypeDisplayName(order.order_type)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Bestelldatum {/* Order Date */}
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(new Date(order.order_date))}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary">
                        Liefertermin {/* Due Date */}
                      </Typography>
                      <Typography variant="body1">
                        {order.due_date ? formatDate(new Date(order.due_date)) : 'Nicht festgelegt'} {/* Not set */}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="text.secondary">
                        Notizen {/* Notes */}
                      </Typography>
                      <Typography variant="body1">
                        {order.notes || 'Keine Notizen'} {/* No notes */}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Lieferanteninformationen {/* Supplier Information */}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  {order.supplier ? (
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Firmenname {/* Company Name */}
                        </Typography>
                        <Typography variant="body1">
                          {order.supplier.name}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Ansprechpartner {/* Contact Person */}
                        </Typography>
                        <Typography variant="body1">
                          {order.supplier.contact_person || 'Nicht verfügbar'} {/* Not available */}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Telefon {/* Phone */}
                        </Typography>
                        <Typography variant="body1">
                          {order.supplier.phone || 'Nicht verfügbar'} {/* Not available */}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          E-Mail {/* Email */}
                        </Typography>
                        <Typography variant="body1">
                          {order.supplier.email || 'Nicht verfügbar'} {/* Not available */}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">
                          Adresse {/* Address */}
                        </Typography>
                        <Typography variant="body1">
                          {order.supplier.address || 'Nicht verfügbar'} {/* Not available */}
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      Lieferanteninformationen nicht verfügbar {/* Supplier information not available */}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Order Summary
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Positions
                      </Typography>
                      <Typography variant="h6">
                        {order.total_positions}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        Total Quantity
                      </Typography>
                      <Typography variant="h6">
                        {order.total_quantity}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        Delivered
                      </Typography>
                      <Typography variant="h6" color="success.main">
                        {order.total_delivered_quantity || 0}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography variant="body2" color="text.secondary">
                        Remaining
                      </Typography>
                      <Typography variant="h6" color="warning.main">
                        {order.total_open_quantity || order.total_quantity}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Positions Tab */}
        <TabPanel value={tabValue} index={1}>
          <PositionEditor 
            order={order}
            onOrderUpdated={handleOrderUpdate}
          />
        </TabPanel>

        {/* Delivery Tab */}
        <TabPanel value={tabValue} index={2}>
          <DeliveryManagement 
            order={order}
            deliveryHistory={deliveryHistory}
            onDeliveryUpdated={handleOrderUpdate}
          />
        </TabPanel>

        {/* Files Tab */}
        <TabPanel value={tabValue} index={3}>
          <FileManagement 
            order={order}
            onFileUpdated={handleOrderUpdate}
          />
        </TabPanel>

        {/* Status Tab */}
        <TabPanel value={tabValue} index={4}>
          <StatusManagement 
            order={order}
            onStatusChange={handleStatusChange}
          />
        </TabPanel>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
